import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ReactComponent as CollapsedIcon } from 'assets/svg/CollapsedIcon.svg';
import { ReactComponent as ExpandedIcon } from 'assets/svg/ExpandedIcon.svg';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';

import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { handleFilterUpdateInUserContext } from 'common/pagination';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { getUserId } from 'utils/utils';

function TeamCalendarsSidebar({ isCollapsed, toggleSidebar }) {
  const {
    firmUserList,
    userList,
    calendarFilters,
    setCalendarFilters,
    setCalendarFiltersWithTracking,
    generateFirmUserIdList,
    isFromCaseSpecificModule,
    isFromLeadSpecificModule,
    isGlobal,
    isClientCalendar,
    userContext,
    highlightedGoogleIconIds,
    setHighlightedGoogleIconIds,
  } = useCalendarContext();
  const [searchQuery, setSearchQuery] = useState('');
  const { addToast } = useToast();

  const extractIdsFromAttendeesFilter = useCallback((filterValue) => {
    if (!Array.isArray(filterValue)) return [];

    return filterValue
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          if (item?.user_group_id) {
            return item.user_group_id;
          }
          if (item?.firm_user_id) {
            return item.firm_user_id;
          }
          return item.value || item.id || item.user_id || item.assignee_id;
        }
        return item;
      })
      .filter(Boolean);
  }, []);

  const [pendingAttendeesFilter, setPendingAttendeesFilter] = useState(() => {
    const filterValue = calendarFilters?.attendeesFilter || [];
    return extractIdsFromAttendeesFilter(filterValue);
  });

  useEffect(() => {
    const filterValue = calendarFilters?.attendeesFilter || [];
    const extractedIds = extractIdsFromAttendeesFilter(filterValue);
    setPendingAttendeesFilter(extractedIds);
  }, [calendarFilters?.attendeesFilter, extractIdsFromAttendeesFilter]);

  const allUsers = useMemo(() => {
    if (!firmUserList || firmUserList.length === 0) return [];

    const isGroupedStructure = firmUserList[0]?.items && Array.isArray(firmUserList[0].items);

    if (isGroupedStructure) {
      return firmUserList.reduce((acc, group) => {
        if (group?.items && Array.isArray(group.items)) {
          return [...acc, ...group.items];
        }
        return acc;
      }, []);
    } else {
      return firmUserList;
    }
  }, [firmUserList]);

  const getUserIdentifier = useCallback(
    (user) => {
      if (user?.res_type === 'user-group') {
        return user?.value || user?.user_group_id;
      }

      if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
        return user?.user_id;
      }
      return user?.assignee_id || user?.user_id || user?.value || user?.client_cognito_username || user?.contact_id || user?.firm_user_id;
    },
    [isFromCaseSpecificModule, isFromLeadSpecificModule]
  );

  const selectedUserIds = useMemo(() => {
    return new Set(pendingAttendeesFilter);
  }, [pendingAttendeesFilter]);

  const selectedUsers = useMemo(() => {
    return allUsers.filter((user) => {
      const userId = getUserIdentifier(user);
      return userId && selectedUserIds.has(userId);
    });
  }, [allUsers, selectedUserIds, getUserIdentifier]);

  const getStableKey = useCallback(
    (user, index) => {
      const preferred = user?.client_cognito_username || user?.contact_id || user?.user_group_id;
      return preferred || getUserIdentifier(user) || `idx-${index}`;
    },
    [getUserIdentifier]
  );

  const visibleGroups = useMemo(() => {
    if (!firmUserList || firmUserList.length === 0) return [];

    const isGroupedStructure = firmUserList[0]?.items && Array.isArray(firmUserList[0].items);

    if (isGroupedStructure) {
      return firmUserList
        .map((group) => {
          if (!group?.items || !Array.isArray(group.items)) return null;

          let filteredItems = group.items;
          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filteredItems = group.items.filter((user) => {
              const fullName = generateFullName(user).toLowerCase();
              const role = (user?._user_type_ || user?.user_type || '').toLowerCase();
              const groupName = (group?.assignee_name || '').toLowerCase();
              return fullName.includes(query) || role.includes(query) || groupName.includes(query);
            });
          }

          const seen = new Set();
          const dedupedItems = filteredItems.filter((user) => {
            const id = getUserIdentifier(user);
            if (!id) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });

          if (dedupedItems.length === 0 && searchQuery.trim()) return null;

          return {
            ...group,
            items: dedupedItems,
          };
        })
        .filter(Boolean);
    } else {
      let filteredItems = firmUserList;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredItems = firmUserList.filter((user) => {
          const fullName = generateFullName(user).toLowerCase();
          const role = (user?._user_type_ || user?.user_type || '').toLowerCase();
          return fullName.includes(query) || role.includes(query);
        });
      }

      const seen = new Set();
      const dedupedItems = filteredItems.filter((user) => {
        const id = getUserIdentifier(user);
        if (!id) return false;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      if (dedupedItems.length === 0) return [];

      return [
        {
          items: dedupedItems,
        },
      ];
    }
  }, [firmUserList, searchQuery, getUserIdentifier]);

  const handleUserToggle = (user) => {
    const userId = getUserIdentifier(user);
    if (!userId) return;

    let userList = [...pendingAttendeesFilter];
    const isCurrentlySelected = selectedUserIds.has(userId);

    if (isCurrentlySelected) {
      userList = userList.filter((id) => id !== userId);
      setHighlightedGoogleIconIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    } else {
      userList = [...userList, userId];
    }

    setPendingAttendeesFilter(userList);
  };

  const handleClear = () => {
    const currentUserId = getUserId();

    let defaultAttendeesFilter = [];
    let defaultUsersFilter = [];

    if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
      defaultAttendeesFilter = [...generateFirmUserIdList()];

      const firmUserListFlat = (firmUserList || []).flatMap((g) => g?.items || []);
      defaultUsersFilter = firmUserListFlat
        .filter((user) => user?.firm_user_id)
        .map((user) => ({
          firm_user_id: user.firm_user_id,
          list_google_event: false,
        }));
    } else {
      const flattenedUserOptions = (userList || []).flatMap((g) => g?.items || []);
      const firmUserListFlat = (firmUserList || []).flatMap((g) => g?.items || []);
      const allUsersAndGroups = [...flattenedUserOptions, ...firmUserListFlat];

      const currentUser = allUsersAndGroups.find((user) => {
        if (user?.firm_user_id) {
          const userId = user?.value || user?.client_cognito_username || user?.contact_id || user?.user_id || user?.firm_user_id;
          return userId === currentUserId || user?.firm_user_id === currentUserId;
        }
        return false;
      });

      defaultAttendeesFilter = currentUserId ? [currentUserId] : [];
      defaultUsersFilter = currentUser?.firm_user_id ? [{ firm_user_id: currentUser.firm_user_id, list_google_event: false }] : [];
    }

    setPendingAttendeesFilter(defaultAttendeesFilter);

    setHighlightedGoogleIconIds(new Set());

    setCalendarFilters((prevValues) => ({
      ...prevValues,
      attendeesFilter: defaultAttendeesFilter,
    }));

    if (isGlobal && userContext) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'users', defaultUsersFilter, true);
    }
  };

  const handleReload = () => {
    if (setCalendarFiltersWithTracking) {
      setCalendarFiltersWithTracking((prevValues) => ({
        ...prevValues,
        attendeesFilter: pendingAttendeesFilter,
      }));
    } else {
      setCalendarFilters((prevValues) => ({
        ...prevValues,
        attendeesFilter: pendingAttendeesFilter,
      }));
    }

    if (isGlobal && userContext) {
      const structuredUsersFilter = [];

      const flattenedUserOptions = (userList || []).flatMap((g) => g?.items || []);
      const firmUserListFlat = (firmUserList || []).flatMap((g) => g?.items || []);
      const allUsersAndGroups = [...flattenedUserOptions, ...firmUserListFlat];
      const processedIds = new Set();

      pendingAttendeesFilter.forEach((selectedId) => {
        if (processedIds.has(selectedId)) return;

        const matchedItem = allUsersAndGroups.find((item) => {
          if (item?.firm_user_id) {
            const userId = item?.value || item?.client_cognito_username || item?.contact_id || item?.user_id || item?.firm_user_id;
            return userId === selectedId || item?.firm_user_id === selectedId;
          } else if (item?.user_group_id) {
            const groupId = item?.value || item?.user_group_id;
            return groupId === selectedId || item?.user_group_id === selectedId;
          }
          return false;
        });

        if (matchedItem) {
          if (matchedItem?.firm_user_id) {
            const userId =
              matchedItem?.value ||
              matchedItem?.client_cognito_username ||
              matchedItem?.contact_id ||
              matchedItem?.user_id ||
              matchedItem?.firm_user_id;
            structuredUsersFilter.push({
              firm_user_id: matchedItem.firm_user_id,
              list_google_event: highlightedGoogleIconIds.has(userId) || highlightedGoogleIconIds.has(matchedItem?.firm_user_id),
            });
            processedIds.add(selectedId);
            processedIds.add(userId);
            processedIds.add(matchedItem.firm_user_id);
          } else if (matchedItem?.user_group_id) {
            const groupId = matchedItem?.value || matchedItem?.user_group_id;
            structuredUsersFilter.push({
              user_group_id: matchedItem.user_group_id,
              list_google_event: highlightedGoogleIconIds.has(groupId) || highlightedGoogleIconIds.has(matchedItem?.user_group_id),
            });
            processedIds.add(selectedId);
            processedIds.add(groupId);
            processedIds.add(matchedItem.user_group_id);
          }
        }
      });

      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'users', structuredUsersFilter, true);
    }
  };

  const getInitials = (user) => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';

    if (firstName || lastName) {
      return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || '?';
    }

    if (user?.group_name) {
      const groupNameWords = user.group_name.trim().split(/\s+/);
      if (groupNameWords.length >= 2) {
        return `${groupNameWords[0].charAt(0)}${groupNameWords[1].charAt(0)}`.toUpperCase();
      } else if (groupNameWords.length === 1) {
        return groupNameWords[0].substring(0, 2).toUpperCase();
      }
      return user.group_name.substring(0, 2).toUpperCase();
    }

    return 'N/A';
  };

  const getColorForUser = (user, index) => {
    if (user?.calendar_color) return user.calendar_color;

    const colors = ['#4CAF50', '#E91E63', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'];
    return colors[index % colors.length];
  };

  if (isCollapsed) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          padding: '12px',
          justifyContent: 'flex-start',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={toggleSidebar}
        >
          <ExpandedIcon style={{ width: 20, height: 20, color: 'var(--black-400)', marginBottom: '12px' }} />
          <Tooltip target=".team-calendars-collapsed-btn" content="Team Calendars" position="right" />
          <div
            className="team-calendars-collapsed-btn"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '2px solid var(--primary-main)',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <i className="pi pi-calendar" style={{ color: 'var(--primary-main)', fontSize: '20px' }}></i>
          </div>
        </div>
      </div>
    );
  }

  const isMobileOverlay = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%', padding: '12px', overflow: 'hidden' }}>
      <div className="d-flex align-items-center justify-content-between mb-3" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center">
          <i className="pi pi-calendar me-2" style={{ color: 'var(--primary-main)', fontSize: '18px' }}></i>
          <h5 className="mb-0" style={{ color: 'var(--primary-main)', fontWeight: 600 }}>
            Team Calendars
          </h5>
        </div>
        {!isMobileOverlay && (
          <span
            role="button"
            tabIndex={0}
            onClick={toggleSidebar}
            aria-label="Collapse sidebar"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            {isCollapsed ? (
              <ExpandedIcon style={{ width: 20, height: 20, color: 'var(--black-400)' }} />
            ) : (
              <CollapsedIcon style={{ width: 20, height: 20, color: 'var(--black-400)' }} />
            )}
          </span>
        )}
      </div>

      <div className="mb-3 pb-3 border-bottom" style={{ flexShrink: 0, width: '100%' }}>
        {selectedUsers.length > 0 && (
          <>
            <div className="d-flex align-items-center mb-2 flex-wrap">
              {selectedUsers.slice(0, 3).map((user, index) => {
                const initials = getInitials(user);
                const color = getColorForUser(user, index);
                return (
                  <Avatar
                    key={getUserIdentifier(user) || index}
                    label={initials}
                    shape="circle"
                    style={{
                      backgroundColor: color,
                      color: '#ffffff',
                      border: `2px solid ${color}`,
                      marginLeft: index > 0 ? '-8px' : '0',
                      zIndex: selectedUsers.length - index,
                    }}
                    size="normal"
                  />
                );
              })}
              {selectedUsers.length > 3 && (
                <Avatar
                  label={`+${selectedUsers.length - 3}`}
                  shape="circle"
                  style={{
                    backgroundColor: '#9c27b0',
                    color: '#ffffff',
                    marginLeft: '-8px',
                    zIndex: 0,
                  }}
                  size="normal"
                />
              )}
              <span className="F-size12 text-muted ms-2">{selectedUsers.length} selected</span>
            </div>

            {selectedUsers.length > 6 && (
              <div
                className="mt-2 mb-2 p-2 rounded"
                style={{
                  backgroundColor: '#FFF3E0',
                  border: '1px solid #FFB74D',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <i className="pi pi-exclamation-circle me-2" style={{ color: '#F57C00', fontSize: '16px' }}></i>
                <span className="F-size12" style={{ color: '#E65100' }}>
                  Loading {selectedUsers.length} calendars may take additional time.
                </span>
              </div>
            )}
          </>
        )}

        <div className="d-flex gap-2">
          <Button label="Reset" className="p-button p-button-secondary outline" onClick={handleClear} style={{ flex: 1 }} />
          {selectedUsers.length === 0 ? (
            <Tooltip target=".update-button-wrapper" content="Select at least one user" position="top" />
          ) : null}
          <span className="update-button-wrapper" style={{ flex: 1, display: 'inline-block' }}>
            <Button
              label="Update"
              className="p-button p-button-secondary"
              onClick={handleReload}
              disabled={selectedUsers.length === 0}
              style={{ width: '100%' }}
            />
          </span>
        </div>
      </div>

      <div className="mb-3" style={{ flexShrink: 0, width: '100%' }}>
        <span className="p-input-icon-left w-100">
          <i className="pi pi-search" />
          <InputText
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search members..."
            className="w-100"
          />
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0, maxHeight: '100%', position: 'relative' }}>
        {visibleGroups.length === 0 ? (
          <div className="text-center text-muted py-4 F-size14">No members found</div>
        ) : (
          visibleGroups.map((group, groupIndex) => {
            const groupKey = group?.user_group_id || group?.assignee_id || group?.assignee_name || `group-${groupIndex}`;

            return (
              <div key={groupKey} style={{ marginBottom: '8px' }}>
                {group.items.map((user, userIndex) => {
                  const userId = getUserIdentifier(user);
                  const safeKey = getStableKey(user, userIndex);
                  const isSelected = userId && selectedUserIds.has(userId);
                  const fullName = generateFullName(user);
                  const color = getColorForUser(user, userIndex);

                  return (
                    <div
                      key={safeKey}
                      className="d-flex align-items-center justify-content-between py-2 px-3 cursor-pointer rounded"
                      style={{
                        backgroundColor: isSelected ? 'var(--primary-bg)' : 'transparent',
                        paddingLeft: '32px',
                        marginBottom: '4px',
                      }}
                      onClick={() => handleUserToggle(user)}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--primary-bg)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                        <div
                          className="rounded-circle me-2"
                          style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: color,
                            flexShrink: 0,
                          }}
                        ></div>
                        <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                          <span className="F-size14" style={{ color: 'var(--black-600)' }}>
                            {fullName}
                          </span>
                        </div>
                        {isSelected && !isClientCalendar && user?.res_type !== 'user-group' && !user?.user_group_id && (
                          <>
                            <div
                              className={`ms-2 cursor-pointer d-flex align-items-center justify-content-center google-calendar-icon-${safeKey}`}
                              style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: highlightedGoogleIconIds.has(userId) ? '#2196F3' : '#f5f5f5',
                                borderRadius: '4px',
                                flexShrink: 0,
                                transition: 'background-color 0.2s ease',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const isHighlighting = !highlightedGoogleIconIds.has(userId);
                                setHighlightedGoogleIconIds((prev) => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(userId)) {
                                    newSet.delete(userId);
                                  } else {
                                    newSet.add(userId);
                                  }
                                  return newSet;
                                });
                                if (isHighlighting) {
                                  addToast(
                                    false,
                                    toastConstant.toasterType.INFO,
                                    toastConstant.api.SUCCESS,
                                    `Google Events selected for ${fullName}`
                                  );
                                } else {
                                  addToast(
                                    false,
                                    toastConstant.toasterType.INFO,
                                    toastConstant.api.SUCCESS,
                                    `Google Events hidden for ${fullName}`
                                  );
                                }
                              }}
                            >
                              <span
                                style={{
                                  color: highlightedGoogleIconIds.has(userId) ? '#ffffff' : '#5f6368',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                }}
                              >
                                G
                              </span>
                            </div>
                            <Tooltip target={`.google-calendar-icon-${safeKey}`} content="Show Google Events" position="left" />
                          </>
                        )}
                      </div>
                      <Checkbox
                        inputId={`member-checkbox-${safeKey}`}
                        checked={isSelected}
                        onChange={() => handleUserToggle(user)}
                        onClick={(e) => e.stopPropagation()}
                        className="ms-2"
                      />
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default TeamCalendarsSidebar;
